import isPropValid from "@emotion/is-prop-valid";
import styled from "@emotion/styled";
import { Link } from "./Link";

type ScrollRowProps = {
  cols?: number;
};

const ScrollRowWrapper = styled.div({
  position: "relative",
  "@media (min-width: 830px)": {
    "&::before,&::after": {
      bottom: "10px",
      content: "''",
      right: "100%",
      position: "absolute",
      top: 0,
      width: "3.2rem",
      zIndex: 10,
      pointerEvents: "none",
      backgroundImage: `linear-gradient(to right, rgba(255, 255, 255, 1) 0%,
rgba(255, 255, 255, 0.738) 15%, rgba(255, 255, 255, 0.541) 30%, rgba(255, 255, 255, 0.382) 47%,
rgba(255, 255, 255, 0.278) 56.5%, rgba(255, 255, 255, 0.194) 65%, rgba(255, 255, 255, 0.126) 73%,
rgba(255, 255, 255, 0.075) 80.2%, rgba(255, 255, 255, 0.042) 86.1%, rgba(255, 255, 255, 0.021) 91%,
rgba(255, 255, 255, 0.008) 95.2%, rgba(255, 255, 255, 0.002) 98.2%,rgba(255, 255, 255, 0) 100%)`,
    },
    "&::before": {
      opacity: 0,
    },
    "&::after": {
      right: "auto",
      left: "100%",
      transform: "scale(-1,1)",
    },
  },
  "@media (min-width: 1440px)": {
    "&::before": {
      opacity: 1,
    },
    "&::after": {
      right: "auto",
      left: "100%",
      transform: "scale(-1,1)",
    },
  },
});
const ScrollRowContent = styled("div", {
  shouldForwardProp: (prop) => isPropValid(prop) && prop !== "cols",
})<ScrollRowProps>(({ cols = 4 }) => ({
  alignItems: "stretch",
  display: "flex",
  flexWrap: "nowrap",
  gap: "10px",
  margin: "-1rem",
  overflowX: "auto",
  overflowY: "visible",
  padding: "1rem",
  scrollSnapAlign: "start",
  scrollSnapType: "inline",
  scrollbarColor: "rgba(0,0,0,.2) rgba(0,0,0,0)",
  scrollbarWidth: "none",
  "&>*": {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: "50vw",
    maxWidth: "50vw",
  },
  "@media (min-width: 420px)": {
    "&>*": {
      flexBasis: "42vw",
      maxWidth: "42vw",
    },
  },
  "@media (min-width: 600px)": {
    marginLeft: "-3.2rem",
    marginRight: "-3.2rem",
    paddingLeft: "3.2rem",
    paddingRight: "3.2rem",
    "&>*": {
      flexBasis: "32vw",
      maxWidth: "32vw",
    },
  },
  "@media (min-width: 768px)": {
    "&>*": {
      flexBasis: "250px",
      maxWidth: "250px",
    },
  },
  "@media (min-width: 1140px)": {
    "&>*": {
      flexBasis: `calc((100% / ${cols}) - (.333rem))`,
      maxWidth: `calc(100% / ${cols} - (.333rem))`,
    },
  },
  "&::WebkitScrollbar": {
    display: "none",
    height: "4px",
  },
  "&::WebkitScrollbar-thumb": {
    backgroundColor: "rgba(0,0,0,.2)",
    borderLeft: "1rem solid white",
    borderRight: "1rem solid white",
    height: "6px",
    "@media (min-width: 600px)": {
      borderRightWidth: "3.2rem",
      borderLeftWidth: "3.2rem",
    },
  },
  "&>div": {
    backgroundColor: "rgba(0,0,0,.1)",
  },
}));

type Props = {
  children: React.ReactNode;
} & ScrollRowProps;

function ScrollRow({ children, cols, ...props }: Props) {
  return (
    <ScrollRowWrapper {...props}>
      <ScrollRowContent cols={cols}>{children}</ScrollRowContent>
    </ScrollRowWrapper>
  );
}

const ScrollRowLink = styled(Link)(({ theme }) => ({
  ...theme.fonts.display,
  alignItems: "center",
  backgroundColor: theme.colors.fg10,
  display: "flex",
  flexDirection: "column",
  fontSize: theme.fontSizes.lg,
  justifyContent: "center",
  padding: "1rem",
  textAlign: "center",
  textDecoration: "none",
  svg: {
    display: "block",
    margin: "1rem 0 0",
  },
}));

ScrollRowLink.defaultProps = {
  arrow: "r",
};

export { ScrollRow, ScrollRowLink };
